/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

//  @ts-nocheck

/* eslint-disable no-unused-vars */
import React, {cloneElement, Component} from 'react';
import ReactDOM from 'react-dom';
import OverlayTrigger from '@react/react-spectrum/OverlayTrigger/js/OverlayTrigger';
import LanguageSwitcherOverlay from './LanguageSwitcherOverlay';

/* eslint-enable no-unused-vars */

/**
 * Find all of the scrollable parents of a DOM node
 */
function getScrollParents(node: any) {
	const nodes = [];
	let currentNode = node;
	while (currentNode.parentNode) {
		const style = window.getComputedStyle(currentNode);

		// Look for scrollable nodes, both real and fake.
		if (/auto|scroll/.test(style.overflow + style.overflowY) || currentNode.hasAttribute('data-scrollable')) {
			nodes.push(currentNode);
		}

		currentNode = currentNode.parentNode;
	}

	const appsSectionContent = document.querySelector('.apps-section-content');
	if (appsSectionContent) {
		nodes.push(appsSectionContent);
	}

	return nodes;
}

class LanguageSwitcherOverlayTrigger extends OverlayTrigger {
	componentDidMount() {
		this._mountNode = document.createElement('div');
		this.renderOverlay();
		// $FlowFixMe
		document.body.addEventListener('mouseUp', this.windowMouseUp);
	}

	onResize = () => {
		// Bug https://jira.corp.adobe.com/browse/CCH-3204
		// Fix by closing the popover when user resize the window

		/* istanbul ignore next */
		if (!window.cchome.util.isMobileOrTabletBrowser()) {
			this.hide();
		}
	};

	onOrientationChange = () => {
		// Bug https://jira.corp.adobe.com/browse/CCH-4791
		// Fix by closing the popover when user rotate the window

		/* istanbul ignore next */
		this.hide();
	};

	show(e: any) {
		if (!this.state.show && !this.props.disabled) {
			this._lastFocus = this.rememberedFocus();
			this.setState({show: true});

			this._scrollParents = getScrollParents(ReactDOM.findDOMNode(this));
			for (const node of this._scrollParents) {
				node.addEventListener('scroll', this.hide, false);
			}

			window.addEventListener('resize', this.onResize);
			window.addEventListener('orientationchange', this.onOrientationChange);

			if (this.props.onShow) {
				this.props.onShow(e);
			}
		}
	}

	componentWillUnmount() {
		super.componentWillUnmount();

		window.removeEventListener('resize', this.onResize);
		window.removeEventListener('orientationchange', this.onOrientationChange);
	}

	makeOverlay(overlay: any, props: any) {
		const {target = this} = this.props;
		const {rootClose = !window.cchome.util.isMobileOrTabletBrowser(), ...overlayProps} = props;
		delete overlayProps.crossOffset;
		delete overlayProps.defaultShow;
		delete overlayProps.flip;
		delete overlayProps.boundariesElement;
		delete overlayProps.shouldUpdatePosition;
		if (!overlay.props.id) {
			overlayProps.id = this.overlayId;
		}
		if (!props.id) {
			props.id = overlay.props.id || overlayProps.id;
		}
		return (
			<LanguageSwitcherOverlay
				{...props}
				show={this.state.show}
				onHide={this.hide}
				target={target}
				rootClose={rootClose}
			>
				{cloneElement(overlay, overlayProps)}
			</LanguageSwitcherOverlay>
		);
	}
}

export default LanguageSwitcherOverlayTrigger;
